import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'

// Estrutura de Cores do Ambiente Vivo
export interface ThemeColors {
    primary: string      // Cor principal da fase (Ex: Laranja Manhã, Roxo Madrugada)
    secondary: string    // Cor de apoio
    accent: string       // Destaque puro
    background: string   // Fundo principal (gradiente)
    surface: string      // Superfícies vidrosas
    text: string         // Texto principal
    textMuted: string    // Texto secundário
    glow: string         // Cor do brilho difuso

    // Parâmetros 3D específicos (opcionais, podem ser derivados)
    particleColor?: string
    gridColor?: string
}

// Configuração completa do Tema
export interface ThemeConfig {
    id: 'morning' | 'afternoon' | 'night' | 'midnight'
    name: string
    colors: ThemeColors
    icon: string
    description: string
}

// Definição dos 4 Temas do Ambiente Vivo
export const THEMES: Record<string, ThemeConfig> = {
    morning: {
        id: 'morning',
        name: 'Manhã',
        icon: '🌅',
        description: 'Suave, início, clareza. Luz difusa e cores pastel.',
        colors: {
            primary: '#fcd34d',   // Amber 300
            secondary: '#60a5fa', // Blue 400
            accent: '#f59e0b',    // Amber 500
            background: '#0f172a', // Slate 900 (ainda escuro, mas amanhecendo)
            surface: 'rgba(255, 255, 255, 0.1)',
            text: '#f1f5f9',
            textMuted: '#94a3b8',
            glow: 'rgba(252, 211, 77, 0.3)',
            particleColor: '#fcd34d',
            gridColor: '#38bdf8'
        }
    },
    afternoon: {
        id: 'afternoon',
        name: 'Tarde',
        icon: '☀️',
        description: 'Vibrante, energia, processo. Contraste alto e saturação.',
        colors: {
            primary: '#06b6d4',   // Cyan 500
            secondary: '#3b82f6', // Blue 500
            accent: '#f472b6',    // Pink 400
            background: '#09090b', // Zinc 950 (fundo escuro para destacar o neon)
            surface: 'rgba(6, 182, 212, 0.08)',
            text: '#ffffff',
            textMuted: '#a1a1aa',
            glow: 'rgba(6, 182, 212, 0.4)',
            particleColor: '#22d3ee', // Cyan 400
            gridColor: '#0891b2'
        }
    },
    night: {
        id: 'night',
        name: 'Noite',
        icon: '🌃',
        description: 'Foco, produção, matéria. Tons profundos e técnicos.',
        colors: {
            primary: '#6366f1',   // Indigo 500
            secondary: '#8b5cf6', // Violet 500
            accent: '#a855f7',    // Purple 500
            background: '#020617', // Slate 950 (quase preto)
            surface: 'rgba(99, 102, 241, 0.05)',
            text: '#e2e8f0',
            textMuted: '#64748b',
            glow: 'rgba(99, 102, 241, 0.25)',
            particleColor: '#818cf8',
            gridColor: '#4f46e5'
        }
    },
    midnight: {
        id: 'midnight',
        name: 'Madrugada',
        icon: '🌑',
        description: 'Silêncio, deep focus. Roxo profundo e mistério.',
        colors: {
            primary: '#a855f7',   // Purple 500
            secondary: '#d946ef', // Fuchsia 500
            accent: '#c026d3',    // Fuchsia 600
            background: '#000000', // Preto absoluto
            surface: 'rgba(168, 85, 247, 0.03)',
            text: '#fdf4ff',
            textMuted: '#701a75',
            glow: 'rgba(168, 85, 247, 0.2)',
            particleColor: '#d8b4fe',
            gridColor: '#9333ea'
        }
    }
}

interface ThemeContextType {
    colors: ThemeColors
    themeName: string // ID do tema atual (efetivo)
    currentTheme: ThemeConfig // Objeto completo do tema
    setTheme: (name: string) => void
    autoMode: boolean
    setAutoMode: (enabled: boolean) => void
    setOverrideTheme: (name: string | null) => void // Para controle programático (ex: Overbot)
}

const ThemeContext = createContext<ThemeContextType | null>(null)

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [baseThemeName, setBaseThemeName] = useState(() => {
        return localStorage.getItem('overpixel-theme') || 'afternoon'
    })
    const [overrideTheme, setOverrideTheme] = useState<string | null>(null)
    
    const [autoMode, setAutoMode] = useState(() => {
        return localStorage.getItem('overpixel-theme-auto') !== 'false' // Padrão true
    })

    // O tema efetivo é o override (se existir) ou o base
    const themeName = overrideTheme || baseThemeName

    // Determinar tema baseado no horário se autoMode estiver ativo
    const getThemeByTime = useCallback(() => {
        const hour = new Date().getHours()
        if (hour >= 5 && hour < 12) return 'morning'
        if (hour >= 12 && hour < 18) return 'afternoon'
        if (hour >= 18 && hour < 24) return 'night'
        return 'midnight' // 0h - 5h
    }, [])

    // Atualizar tema automaticamente (afeta apenas o baseThemeName)
    useEffect(() => {
        if (!autoMode) return

        const update = () => {
            const timeTheme = getThemeByTime()
            if (timeTheme !== baseThemeName) {
                setBaseThemeName(timeTheme)
            }
        }

        update() // Executar imediatamente
        const interval = setInterval(update, 60000) // Verificar a cada minuto
        return () => clearInterval(interval)
    }, [autoMode, baseThemeName, getThemeByTime])

    // Aplicar variáveis CSS quando o tema mudar
    useEffect(() => {
        const theme = THEMES[themeName] || THEMES.afternoon
        const root = document.documentElement

        // Aplicar CSS Variables para uso global e no chat
        root.style.setProperty('--phase-primary', theme.colors.primary)
        root.style.setProperty('--phase-secondary', theme.colors.secondary)
        root.style.setProperty('--phase-accent', theme.colors.accent)
        root.style.setProperty('--phase-background', theme.colors.background)
        root.style.setProperty('--phase-surface', theme.colors.surface)
        root.style.setProperty('--phase-text', theme.colors.text)
        root.style.setProperty('--phase-text-muted', theme.colors.textMuted)
        root.style.setProperty('--phase-glow', theme.colors.glow)

        // Gradiente de fundo suave
        root.style.setProperty('--phase-gradient', `linear-gradient(180deg, ${theme.colors.background} 0%, #000000 100%)`)

        // Persistir escolha manual
        if (!autoMode) {
            localStorage.setItem('overpixel-theme', themeName)
        }
    }, [themeName, autoMode])

    // Função pública para trocar tema (desativa autoMode se for manual)
    const handleSetTheme = useCallback((name: string) => {
        if (THEMES[name]) {
            setBaseThemeName(name)
            // Se usuário escolheu manualmente, desativa modo automático (exceto se for reativar auto)
            // Aqui assumimos que setTheme é uma ação manual explícita
            setAutoMode(false)
            localStorage.setItem('overpixel-theme', name)
            localStorage.setItem('overpixel-theme-auto', 'false')
            
            // Limpa override ao definir manual, para dar feedback imediato
            setOverrideTheme(null)
        }
    }, [])

    // Função para reativar modo automático
    const handleSetAutoMode = useCallback((enabled: boolean) => {
        setAutoMode(enabled)
        localStorage.setItem('overpixel-theme-auto', String(enabled))
        if (enabled) {
            setBaseThemeName(getThemeByTime())
        }
        setOverrideTheme(null) // Reseta override ao mudar modo
    }, [getThemeByTime])

    const currentTheme = THEMES[themeName] || THEMES.afternoon

    return (
        <ThemeContext.Provider value={{
            colors: currentTheme.colors,
            themeName,
            currentTheme,
            setTheme: handleSetTheme,
            autoMode,
            setAutoMode: handleSetAutoMode,
            setOverrideTheme
        }}>
            {children}
        </ThemeContext.Provider>
    )
}

// Hook para usar o tema
export function useTheme(): ThemeContextType {
    const context = useContext(ThemeContext)
    if (!context) {
        throw new Error('useTheme must be used within ThemeProvider')
    }
    return context
}

// Hook para obter apenas cores (útil para componentes isolados)
export function useThemeColors(): ThemeColors {
    const { colors } = useTheme()
    return colors
}
